/**
 * Form Builder AQ Assist — local chat sessions (usage quota is server-side).
 */

const SESSIONS_KEY = "aq_esource_form_assist_sessions_v1";
const USAGE_KEY = "aq_esource_form_assist_usage_v1";
const SESSION_LIMIT = 20;
const MESSAGE_LIMIT = 40;

function safeParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function storageKey(base, organizationId, staffId) {
  const org = String(organizationId || "org").trim() || "org";
  const staff = String(staffId || "staff").trim() || "staff";
  return `${base}:${org}:${staff}`;
}

function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function newFormAssistSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `fb-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function readFormAssistSessions(organizationId, staffId) {
  if (typeof window === "undefined") return [];
  const key = storageKey(SESSIONS_KEY, organizationId, staffId);
  const list = safeParse(window.localStorage.getItem(key), []);
  if (!Array.isArray(list)) return [];
  return list
    .filter((s) => s && s.id && Array.isArray(s.messages))
    .slice(0, SESSION_LIMIT);
}

function writeFormAssistSessions(organizationId, staffId, sessions) {
  if (typeof window === "undefined") return;
  const key = storageKey(SESSIONS_KEY, organizationId, staffId);
  window.localStorage.setItem(
    key,
    JSON.stringify((sessions || []).slice(0, SESSION_LIMIT)),
  );
}

function sessionTitleFromMessages(messages) {
  const firstUser = (messages || []).find((m) => m?.role === "user");
  const text = String(firstUser?.text || "").trim();
  if (!text) return "New chat";
  return text.length > 72 ? `${text.slice(0, 70)}…` : text;
}

/**
 * Upsert the active chat after a successful exchange.
 */
export function upsertFormAssistSession({
  organizationId,
  staffId,
  sessionId,
  messages,
}) {
  const id = String(sessionId || "").trim();
  if (!id) return readFormAssistSessions(organizationId, staffId);

  const trimmedMessages = (messages || [])
    .filter((m) => m && m.id && m.role && m.text)
    .slice(-MESSAGE_LIMIT)
    .map((m) => ({
      id: String(m.id),
      role: m.role === "assistant" ? "assistant" : "user",
      text: String(m.text || ""),
      blocked: !!m.blocked,
      error: !!m.error,
      applied: !!m.applied,
      fieldLabels: Array.isArray(m.fieldLabels)
        ? m.fieldLabels.map(String).slice(0, 12)
        : undefined,
    }));

  if (!trimmedMessages.length) {
    return readFormAssistSessions(organizationId, staffId);
  }

  const nextItem = {
    id,
    title: sessionTitleFromMessages(trimmedMessages),
    messages: trimmedMessages,
    updatedAt: Date.now(),
  };

  const prev = readFormAssistSessions(organizationId, staffId).filter(
    (s) => s.id !== id,
  );
  const next = [nextItem, ...prev].slice(0, SESSION_LIMIT);
  writeFormAssistSessions(organizationId, staffId, next);
  return next;
}

export function getFormAssistSession(organizationId, staffId, sessionId) {
  const id = String(sessionId || "").trim();
  if (!id) return null;
  return (
    readFormAssistSessions(organizationId, staffId).find((s) => s.id === id) ||
    null
  );
}

export function readFormAssistUsageToday(organizationId, staffId) {
  if (typeof window === "undefined") {
    return { date: todayKey(), used: 0 };
  }
  const key = storageKey(USAGE_KEY, organizationId, staffId);
  const raw = safeParse(window.localStorage.getItem(key), null);
  const date = todayKey();
  if (!raw || raw.date !== date) {
    return { date, used: 0 };
  }
  const used = Number(raw.used);
  return { date, used: Number.isFinite(used) && used > 0 ? used : 0 };
}

export function bumpFormAssistUsageToday(organizationId, staffId) {
  const current = readFormAssistUsageToday(organizationId, staffId);
  const next = { date: todayKey(), used: current.used + 1 };
  if (typeof window !== "undefined") {
    const key = storageKey(USAGE_KEY, organizationId, staffId);
    window.localStorage.setItem(key, JSON.stringify(next));
  }
  return next;
}

export function formatFormAssistSessionMeta(session) {
  const ts = Number(session?.updatedAt) || 0;
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
